import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { soaApi } from '@/services/api';
import { SoaMissingClient } from '@/types/soa';
import axios from 'axios';
import { ActiveStatus, Button } from '@/components/ui';
import { formatDate } from '@/utils/formatters';

const getErrorMessage = (err: unknown, fallback: string): string =>
    axios.isAxiosError(err) ? (err.response?.data?.detail || fallback) : fallback;

type SoaClientActiveFilter = '' | 'true' | 'false';

const ACTIVE_FILTERS: { value: SoaClientActiveFilter; label: string }[] = [
    { value: '', label: 'All' },
    { value: 'true', label: 'Active' },
    { value: 'false', label: 'Inactive' },
];

interface SoaMissingClientsDialogProps {
    open: boolean;
    periodEnd: string;
    faSource?: string;
    schemeId?: number;
    onClose: () => void;
}

const SoaMissingClientsDialog: React.FC<SoaMissingClientsDialogProps> = ({
    open, periodEnd, faSource, schemeId, onClose,
}) => {
    const [clients, setClients] = useState<SoaMissingClient[]>([]);
    // The server's total, which the loaded rows fall short of once the gap exceeds
    // one page. Showing clients.length as the total would understate it silently.
    const [total, setTotal] = useState(0);
    const [search, setSearch] = useState('');
    const [activeFilter, setActiveFilter] = useState<SoaClientActiveFilter>('');
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const params = useCallback(
        () => ({
            period_end: periodEnd,
            fa_source: faSource,
            scheme: schemeId,
            client_active: activeFilter || undefined,
            // The popup shows the whole gap at once; the endpoint paginates at 100.
            page_size: 1000,
        }),
        [periodEnd, faSource, schemeId, activeFilter],
    );

    // A stale search term from the last time the dialog was open reads as an empty
    // gap report.
    useEffect(() => {
        if (!open) setSearch('');
    }, [open]);

    useEffect(() => {
        if (!open || !periodEnd) return;
        setLoading(true);
        setError(null);
        soaApi.getMissingClients(params())
            .then(response => {
                setClients(response.results);
                setTotal(response.count);
            })
            .catch(err => {
                setError(getErrorMessage(err, 'Could not load the missing clients.'));
                // The dialog stays mounted across opens, so rows from a previous
                // month would otherwise render under the new month's title.
                setClients([]);
                setTotal(0);
            })
            .finally(() => setLoading(false));
    }, [open, periodEnd, params]);

    // Client-side: the whole list is already loaded, so filtering is instant and
    // every token must match somewhere, making word order irrelevant.
    const visible = useMemo(() => {
        const tokens = search.toLowerCase().split(' ').filter(Boolean);
        if (!tokens.length) return clients;
        return clients.filter(client => {
            const haystack = [
                client.client_code, client.full_name, client.fa_account_no,
                client.email, client.scheme_code,
            ].filter(Boolean).join(' ').toLowerCase();
            return tokens.every(token => haystack.includes(token));
        });
    }, [clients, search]);

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const blob = await soaApi.downloadMissingClients(params());
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `soa_missing_clients_${periodEnd}.csv`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            setError(getErrorMessage(err, 'Could not download the list.'));
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Statements Not Received — {formatDate(periodEnd)}</DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
                    <TextField
                        size="small"
                        placeholder="Search code, name, FA account, email…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        sx={{ flex: 1 }}
                    />
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={activeFilter}
                        onChange={(_, value) => value !== null && setActiveFilter(value)}
                    >
                        {ACTIVE_FILTERS.map(option => (
                            <ToggleButton key={option.label} value={option.value}>
                                {option.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </div>

                {loading && <Typography variant="body2">Loading…</Typography>}

                {!loading && !error && clients.length === 0 && (
                    <Alert severity="success">
                        Every client in this view has a statement for this month.
                    </Alert>
                )}

                {!loading && clients.length > 0 && visible.length === 0 && (
                    <Alert severity="info">No client here matches “{search}”.</Alert>
                )}

                {!loading && !error && visible.length > 0 && (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            <strong>{search ? visible.length : total}</strong>
                            {search ? ` of ${total}` : ''} client
                            {(search ? visible.length : total) === 1 ? ' has' : 's have'} no
                            statement for this month.
                            Chase these with the custodian before sending.
                            {clients.length < total
                                && ` Showing the first ${clients.length} — the CSV has all of them.`}
                            {search && ' The CSV covers the full list, not the search.'}
                        </Typography>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Scheme</TableCell>
                                    <TableCell>Client Code</TableCell>
                                    <TableCell>Client Name</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>FA Account</TableCell>
                                    <TableCell>Email</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visible.map(client => (
                                    <TableRow key={client.id}>
                                        <TableCell>{client.scheme_code || '—'}</TableCell>
                                        <TableCell>{client.client_code}</TableCell>
                                        <TableCell>{client.full_name}</TableCell>
                                        <TableCell>
                                            <ActiveStatus active={client.is_active} />
                                        </TableCell>
                                        <TableCell>{client.fa_account_no || '—'}</TableCell>
                                        <TableCell>{client.email || '—'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleDownload}
                    disabled={downloading || loading || clients.length === 0}
                >
                    {downloading ? 'Preparing…' : 'Download CSV'}
                </Button>
                <Button variant="primary" onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default SoaMissingClientsDialog;
